import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import DashboardLayout from "./DashboardLayout";
import { AuthenticatedMedia, openAuthenticatedMedia } from "./AuthenticatedMedia";
import { deleteDonation, DonationRow, fetchDonations, updateDonation, verifyDonationPayment, fetchDocumentReadiness, fetchDocumentEmailReadiness, fetchDocumentEmailStatus, sendDocumentEmail, issueDonationDocument, updateDonationPreArn } from "./api";
import ActionToast, { ToastAction } from "./ActionToast";
import SignatureImage from "../images/Signature-Of-srisailam.png";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import "./donations.css";

const COUNTRY_OPTIONS = ["India", "Other"];
const INDIAN_STATES = [
  "Andhra Pradesh",
  "Arunachal Pradesh",
  "Assam",
  "Bihar",
  "Chhattisgarh",
  "Goa",
  "Gujarat",
  "Haryana",
  "Himachal Pradesh",
  "Jharkhand",
  "Karnataka",
  "Kerala",
  "Madhya Pradesh",
  "Maharashtra",
  "Manipur",
  "Meghalaya",
  "Mizoram",
  "Nagaland",
  "Odisha",
  "Punjab",
  "Rajasthan",
  "Sikkim",
  "Tamil Nadu",
  "Telangana",
  "Tripura",
  "Uttar Pradesh",
  "Uttarakhand",
  "West Bengal",
  "Andaman and Nicobar Islands",
  "Chandigarh",
  "Dadra and Nagar Haveli and Daman and Diu",
  "Delhi",
  "Jammu and Kashmir",
  "Ladakh",
  "Lakshadweep",
  "Puducherry",
];

const newestDonationsFirst = (donations: DonationRow[]) => [...donations].sort((left, right) => {
  const leftTime = Date.parse(left.submittedAt || left.createdAt || '');
  const rightTime = Date.parse(right.submittedAt || right.createdAt || '');
  const normalizedLeftTime = Number.isFinite(leftTime) ? leftTime : Number.NEGATIVE_INFINITY;
  const normalizedRightTime = Number.isFinite(rightTime) ? rightTime : Number.NEGATIVE_INFINITY;
  if (normalizedLeftTime !== normalizedRightTime) return normalizedRightTime - normalizedLeftTime;
  return Number(right.id || 0) - Number(left.id || 0);
});

const paymentStatusBadge = (paymentStatus?: string | null) => {
  const status = String(paymentStatus || '').trim().toLowerCase();
  if (status === 'verified') return { label: 'Verified', className: 'payment-status-badge payment-status-verified' };
  if (status === 'rejected' || status === 'failed') return { label: 'Rejected/Failed', className: 'payment-status-badge payment-status-rejected' };
  return { label: 'Pending Verification', className: 'payment-status-badge payment-status-pending' };
};

const DonationList: React.FC = () => {
  const [rows, setRows] = useState<DonationRow[]>([]);
  const [search, setSearch] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [viewRow, setViewRow] = useState<DonationRow | null>(null);
  const [editRow, setEditRow] = useState<DonationRow | null>(null);
  const [documentRow, setDocumentRow] = useState<DonationRow | null>(null);
  const [verificationRow, setVerificationRow] = useState<DonationRow | null>(null);
  const [paymentDate, setPaymentDate] = useState("");
  const [verifiedAmount, setVerifiedAmount] = useState("");
  const [transactionReference, setTransactionReference] = useState("");
  const [manualConfirmation, setManualConfirmation] = useState(false);
  const [verificationBusy, setVerificationBusy] = useState(false);
  const [verificationError, setVerificationError] = useState("");
  const [verificationSuccess, setVerificationSuccess] = useState("");
  const dialogRef = useRef<HTMLDivElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const [showStateOptions, setShowStateOptions] = useState(false);
  const [stateSearchValue, setStateSearchValue] = useState("");
  const [saving, setSaving] = useState(false);
  const [attachmentError, setAttachmentError] = useState('');
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [showCertificateModal, setShowCertificateModal] = useState(false);
  const [emailDocument, setEmailDocument] = useState<'receipt' | 'certificate' | null>(null);
  const [emailReadinessMessage, setEmailReadinessMessage] = useState('Checking email availability...');
  const [emailPersonalMessage, setEmailPersonalMessage] = useState('');
  const [emailConfirmed, setEmailConfirmed] = useState(false);
  const [emailReady, setEmailReady] = useState(false);
  const [emailState, setEmailState] = useState('none');
  const [emailAcceptedCount, setEmailAcceptedCount] = useState(0);
  const [emailResend, setEmailResend] = useState(false);
  const [emailUnknownReviewed, setEmailUnknownReviewed] = useState(false);
  const [emailBusy, setEmailBusy] = useState(false);
  const [issueCertificateNumber, setIssueCertificateNumber] = useState('');
  const [issueReceiptNumber, setIssueReceiptNumber] = useState('');
  const [preArn, setPreArn] = useState('');
  const [documentsReady, setDocumentsReady] = useState(false);
  const [documentBusy, setDocumentBusy] = useState(false);
  const [documentMessage, setDocumentMessage] = useState('');
  const [toastAction, setToastAction] = useState<ToastAction | null>(null);
  const documentId = documentRow?.id;
  const documentReceiptNumber = documentRow?.receiptNo || '';
  const documentCertificateNumber = documentRow?.certificateNo || '';
  const documentPreArn = documentRow?.preARN || '';

  const openDonationAttachment = async (id: number) => {
    setAttachmentError('');
    try { await openAuthenticatedMedia(`/files/donations/${id}/photo`); }
    catch { setAttachmentError('The payment attachment is unavailable or your admin session has expired.'); }
  };

  useEffect(() => {
    if (!emailDocument) return;
    let active = true;
    setEmailPersonalMessage(''); setEmailConfirmed(false); setEmailResend(false); setEmailUnknownReviewed(false); setEmailReady(false); setEmailState('none'); setEmailAcceptedCount(0); setEmailReadinessMessage('Checking email availability...');
    fetchDocumentEmailReadiness().then(async result => {
      if (!result.ready || result.canSend === false || !documentId) { if (active) setEmailReadinessMessage(result.message || 'Email sending is unavailable.'); return; }
      const status = await fetchDocumentEmailStatus(documentId, emailDocument);
      if (active) { setEmailState(status.state); setEmailAcceptedCount(status.acceptedCount); setEmailReady(true); setEmailReadinessMessage(status.state === 'pending' ? 'Previous attempt is pending and requires administrator reconciliation.' : ['unknown','uncertain'].includes(status.state) ? 'Previous outcome is unknown. Review it before retrying; the donor may receive a duplicate.' : status.acceptedCount ? 'Previously accepted by SMTP; delivery is not confirmed.' : 'Confirm the recipient before sending.'); }
    }).catch(() => { if (active) setEmailReadinessMessage('Email availability or delivery status could not be confirmed. Sending remains disabled.'); });
    return () => { active = false; };
  }, [emailDocument, documentId]);

  const onSendEmail = async () => {
    if (!emailDocument || !documentRow || !emailReady || !emailConfirmed || emailBusy) return;
    setEmailBusy(true); setEmailReadinessMessage('Sending...');
    try {
      const result = await sendDocumentEmail(documentRow.id, emailDocument, { recipient: documentRow.email || '', personalMessage: emailPersonalMessage, confirmRecipient: true, resendConfirmed: emailResend, unknownReviewed: emailUnknownReviewed });
      setEmailReadinessMessage(result.message);
      const status = await fetchDocumentEmailStatus(documentRow.id, emailDocument);
      setEmailState(status.state); setEmailAcceptedCount(status.acceptedCount); setEmailResend(false); setEmailUnknownReviewed(false); setEmailConfirmed(false);
    } catch (error: any) {
      setEmailUnknownReviewed(false);
      setEmailReadinessMessage(error.message || 'Email failed. Check delivery status before retrying.');
      try { const status = await fetchDocumentEmailStatus(documentRow.id, emailDocument); setEmailState(status.state); setEmailAcceptedCount(status.acceptedCount); }
      catch { setEmailReady(false); }
    } finally { setEmailBusy(false); }
  };

  useEffect(() => {
     if (!documentId) return;
    let active = true;
     setIssueCertificateNumber(documentCertificateNumber); setIssueReceiptNumber(documentReceiptNumber); setPreArn(documentPreArn);
    setDocumentsReady(false); setDocumentMessage('Checking document availability...');
    fetchDocumentReadiness().then(result => { if (active) { setDocumentsReady(result.ready); setDocumentMessage(result.ready ? '' : 'Document schema is not ready. Check the migration and restart the backend.'); } }).catch((err: unknown) => { if (active) setDocumentMessage(err instanceof Error ? err.message : 'Document availability could not be confirmed.'); });
    return () => { active = false; };
  }, [documentCertificateNumber, documentId, documentPreArn, documentReceiptNumber]);

  const saveDocument = async (type: 'receipt' | 'certificate' | 'prearn') => {
    if (!documentRow || !documentsReady || documentBusy) return;
    setDocumentBusy(true); setDocumentMessage('Saving...');
    let saved = false;
    try {
      if (type === 'prearn') await updateDonationPreArn(documentRow.id, preArn);
      else await issueDonationDocument(documentRow.id, type, type === 'receipt' ? issueReceiptNumber.trim() : issueCertificateNumber.trim());
      saved = true;
      const fresh = await fetchDonations();
      setRows(newestDonationsFirst(fresh));
      const updated = fresh.find(row => row.id === documentRow.id);
      if (!updated) throw new Error('Saved, but the donation could not be refreshed. Please reload.');
      setDocumentRow(updated); setDocumentMessage('Saved successfully.');
    } catch (err: any) { setDocumentMessage(saved ? 'Saved, but refresh failed. Close and reload the list before trying again.' : (err.message || 'Unable to save. Please retry.')); if (saved) setDocumentsReady(false); }
    finally { setDocumentBusy(false); }
  };

  const loadDonations = useCallback(async () => {
    setLoading(true);
    setError("");
    try { setRows(newestDonationsFirst(await fetchDonations())); }
    catch (err: any) { setError(err.message || "Failed to fetch donations"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { void loadDonations(); }, [loadDonations]);

  const activeDialog = emailDocument ? "email" : showReceiptModal ? "receipt" : showCertificateModal ? "certificate" : documentRow ? "documents" : verificationRow ? "verification" : editRow ? "edit" : viewRow ? "view" : null;
  useEffect(() => {
    if (!activeDialog) return;
    dialogRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        if (activeDialog === "email") setEmailDocument(null);
        else if (activeDialog === "receipt") setShowReceiptModal(false);
        else if (activeDialog === "certificate") setShowCertificateModal(false);
        else if (activeDialog === "documents") setDocumentRow(null);
        else if (activeDialog === "verification") { if (!verificationBusy) { setViewRow(verificationRow); setVerificationRow(null); } }
        else if (activeDialog === "edit") setEditRow(null);
        else setViewRow(null);
      }
      if (event.key === "Tab" && dialogRef.current) {
        const controls = Array.from(dialogRef.current.querySelectorAll<HTMLElement>('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled])'));
        if (!controls.length) { event.preventDefault(); return; }
        const first = controls[0], last = controls[controls.length - 1];
        if (event.shiftKey && (document.activeElement === first || document.activeElement === dialogRef.current)) { event.preventDefault(); last.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [activeDialog, verificationBusy, verificationRow]);
  useEffect(() => { if (!activeDialog) returnFocusRef.current?.focus(); }, [activeDialog]);

  useEffect(() => {
    if (!toastAction) return;
    const timer = setTimeout(() => setToastAction(null), 6500);
    return () => clearTimeout(timer);
  }, [toastAction]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        r.fullName?.toLowerCase().includes(q) ||
        String(r.receiptNo || "").toLowerCase().includes(q)
    );
  }, [rows, search]);

  const total = useMemo(() => filtered.reduce((acc, row) => acc + Number(row.amount || 0), 0), [filtered]);

  const onDelete = async (id: number) => {
    if (!window.confirm("Permanently delete this donation's database record? This cannot be undone.")) return;
    try {
      await deleteDonation(id);
      setRows((prev) => prev.filter((row) => row.id !== id));
      setToastAction("deleted");
    } catch (err: any) {
      alert(err.message || "Delete failed");
    }
  };

  const openVerification = (row: DonationRow) => {
    setPaymentDate(""); setVerifiedAmount(String(row.amount)); setTransactionReference("");
    setManualConfirmation(false); setVerificationError(""); setVerificationSuccess("");
    setVerificationRow(row); setViewRow(null);
  };
  const closeVerification = () => {
    if (verificationBusy) return;
    setViewRow(verificationRow); setVerificationRow(null);
  };
  const verificationDate = /^\d{4}-\d{2}-\d{2}$/.test(paymentDate) ? new Date(`${paymentDate}T00:00:00.000Z`) : new Date(NaN);
  const dateError = !paymentDate ? 'Enter the actual payment date.' : Number.isNaN(verificationDate.getTime()) || verificationDate.toISOString().slice(0, 10) !== paymentDate || paymentDate > new Date().toISOString().slice(0, 10) ? 'Enter a valid date that is not in the future.' : '';
  const parsedAmount = Number(verifiedAmount);
  const amountError = !verifiedAmount.trim() || !Number.isSafeInteger(parsedAmount) || parsedAmount < 1 || parsedAmount > 2147483647 ? 'Enter a valid whole-rupee amount.' : verificationRow && parsedAmount !== Number(verificationRow.amount) ? 'The verified amount must match the saved donation amount.' : '';
  const trimmedReference = transactionReference.trim();
  const hasReferenceControlCharacter = Array.from(trimmedReference).some(character => {
    const code = character.charCodeAt(0);
    return code < 32 || code === 127;
  });
  const referenceError = !trimmedReference ? 'Enter the bank/UPI transaction reference.' : trimmedReference.length > 255 || hasReferenceControlCharacter ? 'Enter a valid transaction reference.' : '';
  const confirmationError = manualConfirmation ? '' : 'Confirm that you independently checked the payment.';
  const verificationInvalid = !!(dateError || amountError || referenceError || confirmationError);
  const submitVerification = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!verificationRow || verificationBusy || verificationSuccess) return;
    const amount = Number(verifiedAmount);
    if (verificationInvalid) {
      setVerificationError("Complete the required fields shown below before confirming verification."); return;
    }
    setVerificationBusy(true); setVerificationError("");
    try {
      const result = await verifyDonationPayment(verificationRow.id, { paymentDate, verifiedAmount: amount, transactionReference: transactionReference.trim(), manualConfirmation: true });
      if (result.payment_status !== 'verified') throw new Error('The server did not confirm verification. Refresh the record before trying again.');
      setVerificationSuccess("Payment verification recorded.");
      setVerificationRow({ ...verificationRow, payment_status: 'verified' });
      try {
        const fresh = await fetchDonations();
        setRows(newestDonationsFirst(fresh));
        const updated = fresh.find((row) => row.id === verificationRow.id);
        if (updated) setVerificationRow(updated);
        else setVerificationError("Verification was recorded, but the updated donation was not found. Refresh the list.");
      } catch {
        setVerificationError("Verification was recorded, but the donation could not be refreshed. Refresh the list.");
      }
    } catch (err: unknown) { setVerificationError(err instanceof Error && err.message ? err.message : "Payment verification failed. Please try again."); }
    finally { setVerificationBusy(false); }
  };

  const onSave = async () => {
    if (!editRow) return;
    const matched = INDIAN_STATES.find((option) => option.toLowerCase() === stateSearchValue.trim().toLowerCase());
    const selectedState = (matched || editRow.state || "").trim();
    const hasValidState = INDIAN_STATES.some((option) => option.toLowerCase() === selectedState.toLowerCase());
    if (!hasValidState) {
      alert("Please select a valid state from the dropdown.");
      return;
    }
    const rowToSave = { ...editRow, state: selectedState };
    setSaving(true);
    try {
      await updateDonation(rowToSave);
      await loadDonations();
      setEditRow(null);
      setShowStateOptions(false);
      setStateSearchValue("");
      setToastAction("saved");
    } catch (err: any) {
      await loadDonations();
      alert(err.message || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  const toInputDate = (value?: string) => {
    if (!value) return "";
    const raw = String(value).trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
    if (/^\d{2}-\d{2}-\d{4}$/.test(raw)) {
      const [day, month, year] = raw.split("-");
      return `${year}-${month}-${day}`;
    }
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return "";
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  const formatDisplayDate = (value?: string | null) => {
    if (!value) return "-";
    const raw = String(value).trim();
    if (/^\d{2}-\d{2}-\d{4}$/.test(raw)) return raw;

    // Plain date values only (YYYY-MM-DD): map directly.
    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
      const [year, month, day] = raw.split("-");
      return `${day}-${month}-${year}`;
    }

    // Timestamp values: render in IST to avoid one-day drift.
    if (raw.includes("T")) {
      const d = new Date(raw);
      if (Number.isNaN(d.getTime())) return raw;
      return d.toLocaleDateString("en-GB", { timeZone: "Asia/Kolkata" });
    }

    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return raw;
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = String(d.getFullYear());
    return `${day}-${month}-${year}`;
  };

  const normalizeFromOptions = (value: string | undefined, options: string[]) => {
    const raw = (value || "").trim();
    if (!raw) return "";
    const found = options.find((option) => option.toLowerCase() === raw.toLowerCase());
    return found || raw;
  };

  const filteredStateOptions = useMemo(() => {
    const q = stateSearchValue.trim().toLowerCase();
    if (!q) return INDIAN_STATES;
    return INDIAN_STATES.filter((option) => option.toLowerCase().includes(q));
  }, [stateSearchValue]);

  const numberToWords = (num: number): string => {
    const units = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine"];
    const teens = ["Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
    const tens = ["", "Ten", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
    const convertLessThanThousand = (n: number): string => {
      if (n === 0) return "";
      if (n < 10) return units[n];
      if (n < 20) return teens[n - 10];
      if (n < 100) return `${tens[Math.floor(n / 10)]} ${units[n % 10]}`.trim();
      return `${units[Math.floor(n / 100)]} Hundred ${convertLessThanThousand(n % 100)}`.trim();
    };
    if (!num) return "Zero Only";
    let n = Math.floor(num);
    let out = "";
    if (n >= 10000000) {
      out += `${convertLessThanThousand(Math.floor(n / 10000000))} Crore `;
      n %= 10000000;
    }
    if (n >= 100000) {
      out += `${convertLessThanThousand(Math.floor(n / 100000))} Lakh `;
      n %= 100000;
    }
    if (n >= 1000) {
      out += `${convertLessThanThousand(Math.floor(n / 1000))} Thousand `;
      n %= 1000;
    }
    if (n > 0) out += convertLessThanThousand(n);
    return `${out.trim()} Only`;
  };

  const generatePDF = async (elementId: string, fileName: string) => {
    const element = document.getElementById(elementId);
    if (!element) {
      alert("Unable to generate PDF. Content not found.");
      return;
    }

    try {
      if (elementId === 'receipt-content' || elementId === 'certificate-content') {
        await Promise.all(Array.from(element.querySelectorAll('img')).map(async (image) => {
          if (typeof image.decode === 'function') return image.decode();
          if (image.complete && image.naturalWidth > 0) return;
          throw new Error('Document images are not ready.');
        }));
      }
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
      });
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

      if (elementId === 'receipt-content' || elementId === 'certificate-content') {
        const scale = Math.min(1, pdf.internal.pageSize.getHeight() / pdfHeight);
        pdf.addImage(imgData, "PNG", (pdfWidth - pdfWidth * scale) / 2, 0, pdfWidth * scale, pdfHeight * scale);
      } else if (pdfHeight <= pdf.internal.pageSize.getHeight()) {
        pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
      } else {
        let heightLeft = pdfHeight;
        let position = 0;
        const pageHeight = pdf.internal.pageSize.getHeight();

        pdf.addImage(imgData, "PNG", 0, position, pdfWidth, pdfHeight);
        heightLeft -= pageHeight;

        while (heightLeft > 0) {
          position = heightLeft - pdfHeight;
          pdf.addPage();
          pdf.addImage(imgData, "PNG", 0, position, pdfWidth, pdfHeight);
          heightLeft -= pageHeight;
        }
      }

      pdf.save(fileName);
    } catch (err) {
      console.error("PDF generation failed:", err);
      alert("Failed to generate PDF. Please try again.");
    }
  };

  return (
    <DashboardLayout>
      <div className="donations-page">
      <ActionToast action={toastAction} />
      <h1 className="list-title">Donations</h1>
      <p className="list-subtitle">
        {loading ? "Loading donation entries…" : error ? "Donation entries unavailable" : `Submitted/recorded entries total: Rs ${total.toLocaleString("en-IN")} from ${filtered.length} entries (not verified funds)`}
      </p>
      {error && <div className="alert alert-danger" role="alert">{error} <button type="button" onClick={loadDonations}>Retry</button></div>}
      {!loading && !error && <>
      <div className="list-toolbar">
        <div className="search-wrap">
          <label htmlFor="donations-search">Search</label>
          <i className="fa-solid fa-magnifying-glass search-icon" />
          <input
            className="form-control search-input search-input-medium"
            id="donations-search"
            placeholder="Search donor name or receipt no..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>
      <div className="table-shell table-responsive">
        <table className="table data-table mb-0">
          <thead>
            <tr>
              <th>Payment attachment</th>
              <th>Receipt No</th>
              <th>Donor</th>
              <th>Mobile</th>
              <th>Amount</th>
              <th>Method</th>
              <th>Payment Status</th>
              <th>Date</th>
              <th className="text-end">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((row) => (
              <tr key={row.id}>
                <td>
                  {row.photoUrl ? (
                    <AuthenticatedMedia
                      endpoint={`/files/donations/${row.id}/photo`}
                      alt="Payment attachment"
                      className="donation-photo-thumb"
                      style={{ width: "72px", height: "72px", maxWidth: "72px", maxHeight: "72px", objectFit: "cover", display: "block" }}
                    />
                  ) : (
                    <span className="text-muted">-</span>
                  )}
                </td>
                <td>{row.receiptNo || "-"}</td>
                <td>{row.fullName}</td>
                <td>{row.mobile}</td>
                <td className="amount-cell">Rs {Number(row.amount || 0).toLocaleString("en-IN")}</td>
                <td>
                  <span className="data-pill">{row.paymentMethod || "-"}</span>
                </td>
                <td><span className={paymentStatusBadge(row.payment_status).className}>{paymentStatusBadge(row.payment_status).label}</span></td>
                <td>{row.createdAt || "-"}</td>
                <td className="text-end">
                  <div className="action-icons">
                    <button type="button" className="icon-btn" title="View" aria-label={`View ${row.fullName}`} onClick={(event) => { returnFocusRef.current = event.currentTarget; setViewRow(row); }}>
                      <i className="fa-regular fa-eye" />
                    </button>
                    <button
                      type="button"
                      className="icon-btn"
                      title="Edit"
                      aria-label={`Edit ${row.fullName}`}
                      onClick={(event) => {
                        returnFocusRef.current = event.currentTarget;
                        setEditRow({ ...row });
                        setStateSearchValue(row.state || "");
                        setShowStateOptions(false);
                      }}
                    >
                      <i className="fa-regular fa-pen-to-square" />
                    </button>
                    <button type="button" className="icon-btn" title="Manage Documents" aria-label={`Manage documents for ${row.fullName}`} onClick={(event) => { returnFocusRef.current = event.currentTarget; setDocumentRow(row); }}>
                      <i className="fa-regular fa-file-lines" />
                    </button>
                    {/* <button type="button" className="icon-btn" title="Receipt" onClick={() => alert("Receipt preview can be added next")}>
                      <i className="fa-regular fa-file-lines" />
                    </button> */}
                    <button type="button" className="icon-btn icon-btn-danger" title="Delete" aria-label={`Delete ${row.fullName}`} onClick={() => onDelete(row.id)}>
                      <i className="fa-regular fa-trash-can" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={9} className="text-center py-4 text-muted">
                  No donations found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      </>}

      {viewRow && (
        <div className="dash-modal-overlay" onClick={() => setViewRow(null)}>
          <div className="dash-modal" role="dialog" aria-modal="true" aria-labelledby="donation-view-title" ref={dialogRef} tabIndex={-1} onClick={(e) => e.stopPropagation()}>
            <div className="dash-modal-header"><h3 id="donation-view-title" className="dash-modal-title">Donation Details</h3><button type="button" className="dash-modal-close" aria-label="Close donation details" onClick={() => setViewRow(null)}>x</button></div>
            <div className="dash-modal-body donation-profile">
              <div className="donation-profile-header"><h4>{viewRow.fullName || "-"}</h4><p>Recorded amount: {viewRow.amount == null ? "-" : `Rs ${Number(viewRow.amount).toLocaleString("en-IN")}`}</p><p>Payment method: {viewRow.paymentMethod || "-"}</p><p>Payment status: {viewRow.payment_status === "verified" ? "Verified" : viewRow.payment_status === "unverified" ? "Unverified" : "Unknown"}</p><p>Receipt No: {viewRow.receiptNo || "-"}</p></div>
              <section className="donation-profile-section"><h4>Donor Information</h4><dl>
                <div><dt>Full Name</dt><dd>{viewRow.fullName || "-"}</dd></div><div><dt>Mobile</dt><dd>{viewRow.mobile || "-"}</dd></div><div><dt>Email</dt><dd>{viewRow.email || "-"}</dd></div><div><dt>Date of Birth</dt><dd>{formatDisplayDate(viewRow.dateOfBirth)}</dd></div><div><dt>PAN No</dt><dd>{viewRow.panNo || "-"}</dd></div>
              </dl></section>
              <section className="donation-profile-section"><h4>Address</h4><dl>
                <div><dt>Country</dt><dd>{viewRow.country || "-"}</dd></div><div><dt>State</dt><dd>{viewRow.state || "-"}</dd></div><div><dt>City</dt><dd>{viewRow.city || "-"}</dd></div><div><dt>Pin Code</dt><dd>{viewRow.pinCode || "-"}</dd></div><div className="donation-profile-wide"><dt>Address</dt><dd>{viewRow.address || "-"}</dd></div>
              </dl></section>
              <section className="donation-profile-section"><h4>Donation Information</h4><dl>
                <div><dt>Amount</dt><dd>{viewRow.amount ?? "-"}</dd></div><div><dt>Payment Method</dt><dd>{viewRow.paymentMethod || "-"}</dd></div><div className="donation-profile-wide"><dt>Message</dt><dd>{viewRow.message || "-"}</dd></div>
                <div><dt>Payment attachment</dt><dd>{viewRow.photoUrl ? <button type="button" onClick={() => void openDonationAttachment(viewRow.id)}>View payment attachment</button> : "-"}{attachmentError && <p role="alert" className="donation-validation-error">{attachmentError}</p>}</dd></div>
              </dl></section>
              <section className="donation-profile-section"><h4>Document References</h4><dl><div><dt>Receipt No</dt><dd>{viewRow.receiptNo || "-"}</dd></div><div><dt>Pre-ARN</dt><dd>{viewRow.preARN || "-"}</dd></div><div><dt>Certificate No</dt><dd>{viewRow.certificateNo || "-"}</dd></div></dl></section>
            </div>
            <div className="dash-modal-footer">
              <button type="button" className="donation-button donation-secondary" onClick={() => setViewRow(null)}>Close</button>
              <button type="button" className="donation-button donation-secondary" onClick={() => { setDocumentRow(viewRow); setViewRow(null); }}>Manage Documents</button>
              <button type="button" className="donation-button donation-secondary" disabled={viewRow.payment_status === "verified"} onClick={() => openVerification(viewRow)}>Verify Payment</button>
              <button type="button" className="donation-button donation-primary" onClick={() => { setEditRow({ ...viewRow }); setStateSearchValue(viewRow.state || ""); setShowStateOptions(false); setViewRow(null); }}>Edit Donation</button>
            </div>
          </div>
        </div>
      )}

      {verificationRow && (
        <div className="dash-modal-overlay" onClick={closeVerification}>
          <div className="dash-modal" role="dialog" aria-modal="true" aria-labelledby="donation-verification-title" ref={dialogRef} tabIndex={-1} onClick={(event) => event.stopPropagation()}>
            <div className="dash-modal-header"><h3 id="donation-verification-title" className="dash-modal-title">Verify Payment</h3><button type="button" className="dash-modal-close" aria-label="Close verification" disabled={verificationBusy} onClick={closeVerification}>x</button></div>
            <form onSubmit={submitVerification} noValidate>
              <div className="dash-modal-body">
                <p>Donor: {verificationRow.fullName || "-"}</p><p>Saved donation amount: Rs {Number(verificationRow.amount).toLocaleString("en-IN")}</p>
                <p>Check the bank or UPI account independently. A payment attachment is not proof of payment. Verification may be unavailable until enabled by an administrator.</p>
                <label htmlFor="verification-date">Actual payment date</label><input id="verification-date" className="form-control" type="date" required aria-invalid={!!dateError} aria-describedby={dateError ? 'verification-date-error' : undefined} value={paymentDate} onChange={(event) => setPaymentDate(event.target.value)} disabled={verificationBusy || !!verificationSuccess} />
                {dateError && <p id="verification-date-error" className="donation-validation-error">{dateError}</p>}
                <label htmlFor="verification-amount">Verified amount</label><input id="verification-amount" className="form-control" type="number" min="1" step="1" required aria-invalid={!!amountError} aria-describedby={amountError ? 'verification-amount-error' : undefined} value={verifiedAmount} onChange={(event) => setVerifiedAmount(event.target.value)} disabled={verificationBusy || !!verificationSuccess} />
                {amountError && <p id="verification-amount-error" className="donation-validation-error">{amountError}</p>}
                <label htmlFor="verification-reference">Bank/UPI transaction reference</label><input id="verification-reference" className="form-control" maxLength={255} required aria-invalid={!!referenceError} aria-describedby={referenceError ? 'verification-reference-error' : undefined} value={transactionReference} onChange={(event) => setTransactionReference(event.target.value)} disabled={verificationBusy || !!verificationSuccess} />
                {referenceError && <p id="verification-reference-error" className="donation-validation-error">{referenceError}</p>}
                <label><input type="checkbox" required aria-invalid={!!confirmationError} aria-describedby={confirmationError ? 'verification-confirmation-error' : undefined} checked={manualConfirmation} onChange={(event) => setManualConfirmation(event.target.checked)} disabled={verificationBusy || !!verificationSuccess} /> I independently checked receipt of this payment in the bank/UPI account.</label>
                {confirmationError && <p id="verification-confirmation-error" className="donation-validation-error">{confirmationError}</p>}
                {verificationInvalid && !verificationSuccess && <p>Complete the required fields above to enable confirmation.</p>}
                {verificationError && <p role="alert">{verificationError}</p>}{verificationSuccess && <p role="status">{verificationSuccess}</p>}
              </div>
              <div className="dash-modal-footer"><button type="button" className="donation-button donation-secondary" disabled={verificationBusy} onClick={closeVerification}>Close</button><button type="submit" className="donation-button donation-primary" disabled={verificationBusy || !!verificationSuccess || verificationInvalid}>{verificationBusy ? "Saving…" : "Confirm Verification"}</button></div>
            </form>
          </div>
        </div>
      )}

      {editRow && (
        <div className="dash-modal-overlay" onClick={() => setEditRow(null)}>
          <div className="dash-modal" role="dialog" aria-modal="true" aria-labelledby="donation-edit-title" ref={dialogRef} tabIndex={-1} onClick={(e) => e.stopPropagation()}>
            <div className="dash-modal-header">
              <h3 id="donation-edit-title" className="dash-modal-title">Edit Donation</h3>
              <button type="button" className="dash-modal-close" aria-label="Close edit donation" onClick={() => setEditRow(null)}>
                x
              </button>
            </div>
            <div className="dash-modal-body">
              <div className="edit-grid">
                <div className="edit-field full">
                  <label className="edit-label">Payment attachment</label>
                  {editRow.photoUrl ? (
                    <AuthenticatedMedia endpoint={`/files/donations/${editRow.id}/photo`} alt="Payment attachment" className="donation-photo-thumb" fallback={<div className="text-muted mb-2">Existing attachment unavailable</div>} />
                  ) : (
                    <div className="text-muted mb-2">No payment attachment</div>
                  )}
                  {editRow.photoFile && <div className="text-muted mb-2">Replacement selected: <strong>{editRow.photoFile.name}</strong></div>}
                  <input
                    type="file"
                    aria-label="Payment attachment"
                    accept="image/*"
                    className="form-control"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      const previewUrl = URL.createObjectURL(file);
                      setEditRow({ ...editRow, photoFile: file, photoUrl: previewUrl });
                    }}
                  />
                </div>
                <div className="edit-field">
                  <label className="edit-label" htmlFor="donation-edit-0">Full Name</label>
                  <input id="donation-edit-0" className="form-control" placeholder="Enter full name" value={editRow.fullName || ""} onChange={(e) => setEditRow({ ...editRow, fullName: e.target.value })} />
                </div>
                <div className="edit-field">
                  <label className="edit-label" htmlFor="donation-edit-1">Mobile</label>
                  <input id="donation-edit-1" className="form-control" placeholder="Enter mobile number" value={editRow.mobile || ""} onChange={(e) => setEditRow({ ...editRow, mobile: e.target.value })} />
                </div>
                <div className="edit-field">
                  <label className="edit-label" htmlFor="donation-edit-2">Email</label>
                  <input id="donation-edit-2" className="form-control" placeholder="Enter email address" value={editRow.email || ""} onChange={(e) => setEditRow({ ...editRow, email: e.target.value })} />
                </div>
                <div className="edit-field">
                  <label className="edit-label" htmlFor="donation-edit-3">Date of Birth</label>
                  <input id="donation-edit-3" className="form-control" type="date" placeholder="Select date of birth" value={toInputDate(editRow.dateOfBirth)} onChange={(e) => setEditRow({ ...editRow, dateOfBirth: e.target.value })} />
                </div>
                <div className="edit-field">
                  <label className="edit-label" htmlFor="donation-edit-4">Country</label>
                  <select id="donation-edit-4" className="form-select" value={normalizeFromOptions(editRow.country, COUNTRY_OPTIONS)} onChange={(e) => setEditRow({ ...editRow, country: e.target.value })}>
                    {COUNTRY_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                    {normalizeFromOptions(editRow.country, COUNTRY_OPTIONS) && !COUNTRY_OPTIONS.includes(normalizeFromOptions(editRow.country, COUNTRY_OPTIONS)) && (
                      <option value={normalizeFromOptions(editRow.country, COUNTRY_OPTIONS)}>{normalizeFromOptions(editRow.country, COUNTRY_OPTIONS)}</option>
                    )}
                  </select>
                </div>
                <div className="edit-field">
                  <label className="edit-label" htmlFor="donation-edit-5">State</label>
                  <div style={{ position: "relative" }}>
                    <input id="donation-edit-5"
                      className="form-control"
                      placeholder="Search or select state"
                      value={stateSearchValue}
                      onFocus={() => setShowStateOptions(true)}
                      onChange={(e) => {
                        setStateSearchValue(e.target.value);
                        setShowStateOptions(true);
                      }}
                      onBlur={() => {
                        const matched = INDIAN_STATES.find((option) => option.toLowerCase() === stateSearchValue.trim().toLowerCase());
                        if (matched) {
                          setStateSearchValue(matched);
                          setEditRow({ ...editRow, state: matched });
                        } else {
                          setStateSearchValue(editRow.state || "");
                        }
                        setTimeout(() => setShowStateOptions(false), 120);
                      }}
                    />
                    {showStateOptions && filteredStateOptions.length > 0 && (
                      <div
                        className="list-group"
                        style={{
                          position: "absolute",
                          zIndex: 20,
                          width: "100%",
                          maxHeight: "180px",
                          overflowY: "auto",
                          border: "1px solid #dfe5ef",
                          borderRadius: "8px",
                          marginTop: "6px",
                          background: "#fff",
                        }}
                      >
                        {filteredStateOptions.slice(0, 50).map((option) => (
                          <button
                            key={option}
                            type="button"
                            className="list-group-item list-group-item-action"
                            onMouseDown={(evt) => evt.preventDefault()}
                            onClick={() => {
                              setStateSearchValue(option);
                              setEditRow({ ...editRow, state: option });
                              setShowStateOptions(false);
                            }}
                          >
                            {option}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="edit-field">
                  <label className="edit-label" htmlFor="donation-edit-6">City</label>
                  <input id="donation-edit-6" className="form-control" placeholder="Enter city" value={editRow.city || ""} onChange={(e) => setEditRow({ ...editRow, city: e.target.value })} />
                </div>
                <div className="edit-field">
                  <label className="edit-label" htmlFor="donation-edit-7">Pin Code</label>
                  <input id="donation-edit-7" className="form-control" placeholder="Enter pin code" value={editRow.pinCode || ""} onChange={(e) => setEditRow({ ...editRow, pinCode: e.target.value })} />
                </div>
                <div className="edit-field full">
                  <label className="edit-label" htmlFor="donation-edit-8">Address</label>
                  <input id="donation-edit-8" className="form-control" placeholder="Enter full address" value={editRow.address || ""} onChange={(e) => setEditRow({ ...editRow, address: e.target.value })} />
                </div>
                <div className="edit-field">
                  <label className="edit-label" htmlFor="donation-edit-9">PAN No</label>
                  <input id="donation-edit-9" className="form-control" placeholder="Enter PAN number" value={editRow.panNo || ""} onChange={(e) => setEditRow({ ...editRow, panNo: e.target.value })} />
                </div>
                <div className="edit-field">
                  <label className="edit-label" htmlFor="donation-edit-10">Amount</label>
                  <input id="donation-edit-10" className="form-control" placeholder="Enter amount" type="number" value={editRow.amount || 0} onChange={(e) => setEditRow({ ...editRow, amount: Number(e.target.value) })} />
                </div>
                <div className="edit-field">
                  <label className="edit-label" htmlFor="donation-edit-11">Payment Method</label>
                  <input id="donation-edit-11" className="form-control" placeholder="Enter payment method" value={editRow.paymentMethod || ""} onChange={(e) => setEditRow({ ...editRow, paymentMethod: e.target.value })} />
                </div>
                <div className="edit-field full">
                  <label className="edit-label" htmlFor="donation-edit-12">Message</label>
                  <textarea id="donation-edit-12" className="form-control" rows={3} placeholder="Enter message" value={editRow.message || ""} onChange={(e) => setEditRow({ ...editRow, message: e.target.value })} />
                </div>
              </div>
            </div>
            <div className="dash-modal-footer">
              <button type="button" className="btn btn-outline-secondary" onClick={() => setEditRow(null)}>
                Cancel
              </button>
              <button type="button" className="btn btn-primary" disabled={saving} onClick={onSave}>
                {saving ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      {documentRow && !showReceiptModal && !showCertificateModal && !emailDocument && (
        <div className="dash-modal-overlay" onClick={() => setDocumentRow(null)}>
          <div className="dash-modal donation-documents-modal" role="dialog" aria-modal="true" aria-labelledby="donation-documents-title" ref={dialogRef} tabIndex={-1} onClick={(e) => e.stopPropagation()}>
            <div className="dash-modal-header"><h3 id="donation-documents-title" className="dash-modal-title">Manage Documents</h3><button type="button" className="dash-modal-close" aria-label="Close manage documents" onClick={() => setDocumentRow(null)}>x</button></div>
            <div className="dash-modal-body donation-documents">
              <p className="document-intro">Unverified donations can be previewed as drafts. Emails cannot be sent yet.</p>
              {documentMessage && <p className="document-notice" role="status">{documentMessage}</p>}
              <section className="document-section"><h4>Pre-ARN</h4>
                {!!documentRow.receipt_issued_at || !!documentRow.certificate_issued_at ? <div className="document-readonly"><span>Pre-ARN</span><strong>{documentRow.preARN || '-'}</strong></div> : <div className="document-field-row"><div className="document-field"><label htmlFor="document-prearn">Pre-ARN</label><input id="document-prearn" value={preArn} maxLength={50} onChange={e => setPreArn(e.target.value)} disabled={!documentsReady || documentBusy} /></div><button type="button" className="donation-button donation-secondary" disabled={!documentsReady || documentBusy} onClick={() => void saveDocument('prearn')}>Save Pre-ARN</button></div>}
              </section>
              <section className="document-section"><div className="document-section-heading"><h4>Donation Receipt</h4><span className="document-meta">Receipt status: {documentRow.receipt_issued_at ? 'Issued' : 'Draft'} · Issue date: {formatDisplayDate(documentRow.receipt_issued_at)}</span></div>
                {documentRow.receipt_issued_at ? <div className="document-readonly"><span>Receipt number</span><strong>{documentRow.receiptNo || '-'}</strong></div> : <div className="document-field"><label htmlFor="receipt-issue-number">Receipt number</label><input id="receipt-issue-number" maxLength={50} value={issueReceiptNumber} onChange={e => setIssueReceiptNumber(e.target.value)} disabled={!documentsReady || documentBusy || !!documentRow.receiptNo} /></div>}
                <div className="document-actions">{!documentRow.receipt_issued_at && <button type="button" className="donation-button donation-secondary" disabled={!documentsReady || documentBusy || documentRow.payment_status !== 'verified' || !issueReceiptNumber.trim()} onClick={() => void saveDocument('receipt')}>Issue Receipt</button>}
                <button type="button" className="donation-button donation-primary" onClick={() => {
                  if (documentRow.payment_status === 'verified' && (!documentRow.payment_date || Number.isNaN(new Date(documentRow.payment_date).getTime()) || !Number.isSafeInteger(Number(documentRow.verified_amount)) || Number(documentRow.verified_amount) <= 0)) { alert("The verified payment has no valid payment date or verified amount."); return; }
                  setShowReceiptModal(true);
                }}>Preview Receipt</button>
                <button type="button" className="donation-button donation-secondary" disabled={documentRow.payment_status !== 'verified' || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(documentRow.email || '')} onClick={() => setEmailDocument('receipt')}>Email Receipt</button></div>
              </section>
              <section className="document-section"><div className="document-section-heading"><h4>Donation Certificate</h4><span className="document-meta">Certificate status: {documentRow.certificate_issued_at ? 'Issued' : 'Draft'} · Issue date: {formatDisplayDate(documentRow.certificate_issued_at)}</span></div>
                {documentRow.certificate_issued_at ? <div className="document-readonly"><span>Certificate number</span><strong>{documentRow.certificateNo || '-'}</strong></div> : <div className="document-field"><label htmlFor="certificate-issue-number">Certificate number from tax department</label><input id="certificate-issue-number" value={issueCertificateNumber} maxLength={50} onChange={(event) => setIssueCertificateNumber(event.target.value)} disabled={!documentsReady || documentBusy || !!documentRow.certificateNo} /></div>}
                <div className="document-actions">{!documentRow.certificate_issued_at && <button type="button" className="donation-button donation-secondary" disabled={!documentsReady || documentBusy || documentRow.payment_status !== 'verified' || !issueCertificateNumber.trim()} onClick={() => void saveDocument('certificate')}>Issue Certificate</button>}
                <button type="button" className="donation-button donation-primary" onClick={() => {
                  if (documentRow.payment_status === 'verified' && (!documentRow.payment_date || Number.isNaN(new Date(documentRow.payment_date).getTime()) || !Number.isSafeInteger(Number(documentRow.verified_amount)) || Number(documentRow.verified_amount) <= 0)) { alert("The verified payment has no valid payment date or verified amount."); return; }
                  setShowCertificateModal(true);
                }}>Preview Certificate</button>
                <button type="button" className="donation-button donation-secondary" disabled={documentRow.payment_status !== 'verified' || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(documentRow.email || '')} onClick={() => setEmailDocument('certificate')}>Email Certificate</button></div>
              </section>
            </div>
            <div className="dash-modal-footer"><button type="button" className="donation-button donation-secondary" onClick={() => setDocumentRow(null)}>Close</button></div>
          </div>
        </div>
      )}

      {emailDocument && documentRow && (
        <div className="dash-modal-overlay" onClick={() => setEmailDocument(null)}>
          <div className="dash-modal" role="dialog" aria-modal="true" aria-labelledby="donation-email-title" ref={dialogRef} tabIndex={-1} onClick={(event) => event.stopPropagation()}>
            <div className="dash-modal-header"><h3 id="donation-email-title">Email {emailDocument === 'receipt' ? 'Receipt' : 'Certificate'}</h3><button type="button" className="dash-modal-close" aria-label="Close email confirmation" onClick={() => setEmailDocument(null)}>x</button></div>
            <div className="dash-modal-body donation-email-body">
              <div className="donation-email-details"><p>Recipient: {documentRow.email}</p><p>Attachment: Donation {emailDocument === 'receipt' ? 'receipt' : 'certificate'} PDF</p><p>Document number: {(emailDocument === 'receipt' ? documentRow.receiptNo : documentRow.certificateNo) || '-'}</p></div>
              <div className="donation-email-message"><label htmlFor="donation-email-message">Optional personal message</label><textarea id="donation-email-message" rows={4} maxLength={2000} value={emailPersonalMessage} onChange={e => setEmailPersonalMessage(e.target.value)} /></div>
              <label className="donation-email-confirm"><input type="checkbox" checked={emailConfirmed} onChange={e => setEmailConfirmed(e.target.checked)} /><span>I confirm the recipient and document.</span></label>
              {emailAcceptedCount > 0 && <label className="donation-email-confirm"><input type="checkbox" checked={emailResend} onChange={e => setEmailResend(e.target.checked)} /><span>I explicitly confirm resending this document.</span></label>}
              {['unknown','uncertain'].includes(emailState) && <label className="donation-email-confirm"><input type="checkbox" checked={emailUnknownReviewed} onChange={e => setEmailUnknownReviewed(e.target.checked)} /><span>I reviewed the unknown outcome and explicitly accept the risk of a duplicate email.</span></label>}
              <p role="status">{emailReadinessMessage}</p>
            </div>
            <div className="dash-modal-footer"><button type="button" className="donation-button donation-secondary" disabled={emailBusy} onClick={() => setEmailDocument(null)}>Close</button><button type="button" className="donation-button donation-primary" disabled={!emailReady || emailBusy || !emailConfirmed || (emailAcceptedCount > 0 && !emailResend) || emailState === 'pending' || (['unknown','uncertain'].includes(emailState) && !emailUnknownReviewed) || documentRow.payment_status !== 'verified' || !(emailDocument === 'receipt' ? documentRow.receipt_issued_at && documentRow.receiptNo : documentRow.certificate_issued_at && documentRow.certificateNo) || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(documentRow.email || '')} onClick={() => void onSendEmail()}>{emailBusy ? 'Sending...' : emailReady ? 'Send email' : 'Send email (unavailable)'}</button></div>
          </div>
        </div>
      )}

      {showReceiptModal && documentRow && (
        <div className="dash-modal-overlay" onClick={() => setShowReceiptModal(false)}>
          <div className="dash-modal" role="dialog" aria-modal="true" aria-labelledby="receipt-preview-title" ref={dialogRef} tabIndex={-1} onClick={(e) => e.stopPropagation()}>
            <div className="dash-modal-header">
              <h3 id="receipt-preview-title" className="dash-modal-title">Donation Receipt - {documentRow.receiptNo}</h3>
              <button type="button" className="dash-modal-close" aria-label="Close receipt preview" onClick={() => setShowReceiptModal(false)}>
                x
              </button>
            </div>
            <div className="dash-modal-body">
              <div id="receipt-content" className="receipt-container">
                {(!documentRow.receipt_issued_at || documentRow.payment_status !== 'verified') && <p className="donation-document-draft">DRAFT / NOT ISSUED — NOT A RECEIPT</p>}
                <div className="donation-document-brand"><img src={`${process.env.PUBLIC_URL}/foundation-icon-512.png`} alt="Srisailam Foundation logo" /><div><strong>SRISAILAM FOUNDATION</strong><p>440/E Tulsi Nagar Phase 1, Near Pragati Puram,Raebareli, Uttar Pradesh 229001</p><p>PAN: ABPCS2866R | 80G Reg. No.: ABPCS2866RF20251</p><p>Phone: +91-9450008921, +91-9838580001 | Email: info@srisailamfoundation.org</p></div></div>

                <h4 className="text-center mb-4 fw-bold" style={{ fontSize: "28px" }}>DONATION RECEIPT</h4>

                <table className="receipt-table" aria-label="Receipt information"><tbody>
                  <tr><th scope="row">Receipt No.:</th><td>{documentRow.receiptNo || "-"}</td></tr>
                  <tr><th scope="row">Pre-Acknowledgement Reference Number (Pre-ARN):</th><td>{documentRow.preARN || "-"}</td></tr>
                  <tr><th scope="row">Payment date:</th><td>{formatDisplayDate(documentRow.payment_date)}</td></tr>
                  <tr><th scope="row">by way of:</th><td>{documentRow.paymentMethod || "-"}</td></tr>
                </tbody></table>
                <h5 className="receipt-section-heading">Donor &amp; Donation Details</h5>
                <table className="receipt-table" aria-label="Donor and donation details"><tbody>
                  <tr><th scope="row">{documentRow.payment_status === 'verified' ? 'Received with thanks from:' : 'Donor:'}</th><td>{documentRow.fullName || "-"}</td></tr>
                  <tr><th scope="row">Address:</th><td>{documentRow.address || "-"}</td></tr>
                  <tr><th scope="row">PAN of Donor:</th><td>{documentRow.panNo || "-"}</td></tr>
                  <tr><th scope="row">a sum of Rs.</th><td>{Number(documentRow.payment_status === 'verified' ? documentRow.verified_amount : documentRow.amount).toLocaleString("en-IN")}</td></tr>
                  <tr><th scope="row">Amount in Words</th><td>Rupees {numberToWords(Number(documentRow.payment_status === 'verified' ? documentRow.verified_amount : documentRow.amount))}</td></tr>
                </tbody></table>
                <p><strong>towards donation to our organization.</strong></p>
                {documentRow.payment_status === 'verified' && <p className="receipt-declaration">This donation is eligible for deduction under Section 80G of the Income Tax Act, 1961.</p>}

                <h5 className="receipt-section-heading">Foundation &amp; 80G Details</h5>
                <table className="receipt-table" aria-label="Foundation and 80G details"><tbody>
                  <tr><th scope="row">80G URN:</th><td>ABPCS2866RF20251</td></tr>
                  <tr><th scope="row">Date of Approval:</th><td>19.04.2025</td></tr>
                  <tr><th scope="row">Validity:</th><td>Valid till 19.04.2028</td></tr>
                  <tr><th scope="row">PAN of NGO:</th><td>ABPCS2866R</td></tr>
                </tbody></table>

                {documentRow.payment_status === 'verified' && <div className="receipt-signature">
                  <p>For SRISAILAM FOUNDATION</p>
                  <img src={SignatureImage} alt="Signature and Seal" width={200} height={100} className="mt-2" />
                  <p className="mt-2">Authorized Signatory</p>
                </div>}

                <p className="donation-document-thanks">
                  Thank you for your generous support!
                </p>
              </div>
            </div>
            <div className="dash-modal-footer">
              <button type="button" className="btn btn-outline-secondary" onClick={() => setShowReceiptModal(false)}>
                Close
              </button>
              <button
                type="button"
                className="btn btn-primary"
                disabled={documentRow.payment_status !== 'verified' || !documentRow.receiptNo || !documentRow.receipt_issued_at}
                onClick={() => generatePDF("receipt-content", `Recepit_${documentRow.receiptNo || "Donation"}.pdf`)}
              >
                Download PDF
              </button>
            </div>
          </div>
        </div>
      )}

      {showCertificateModal && documentRow && (
        <div className="dash-modal-overlay" onClick={() => setShowCertificateModal(false)}>
          <div className="dash-modal" role="dialog" aria-modal="true" aria-labelledby="certificate-preview-title" ref={dialogRef} tabIndex={-1} onClick={(e) => e.stopPropagation()}>
            <div className="dash-modal-header">
              <h3 id="certificate-preview-title" className="dash-modal-title">Donation Certificate - {documentRow.certificateNo}</h3>
              <button type="button" className="dash-modal-close" aria-label="Close certificate preview" onClick={() => setShowCertificateModal(false)}>
                x
              </button>
            </div>
            <div className="dash-modal-body">
              <div id="certificate-content" className="certificate-container">
                {(!documentRow.certificate_issued_at || documentRow.payment_status !== 'verified') && <p className="donation-document-draft">DRAFT / {documentRow.payment_status === 'verified' ? 'NOT ISSUED' : 'UNVERIFIED'} — NOT A CERTIFICATE</p>}
                <div className="donation-document-brand"><img src={`${process.env.PUBLIC_URL}/foundation-icon-512.png`} alt="Srisailam Foundation logo" /><div><strong>SRISAILAM FOUNDATION</strong><p>440/E Tulsi Nagar Phase 1, Near Pragati Puram,Raebareli, Uttar Pradesh 229001</p><p>PAN: ABPCS2866R | 80G Reg. No.: ABPCS2866RF20251</p><p>Phone: +91-9450008921, +91-9838580001 | Email: info@srisailamfoundation.org</p></div></div>

                <h4 className="text-center mb-2 fw-bold" style={{ fontSize: "28px" }}>DONATION CERTIFICATE</h4>

                <table className="certificate-table" aria-label="Certificate information"><tbody>
                  <tr><th scope="row">Certificate No.:</th><td>{documentRow.certificateNo || "-"}</td></tr>
                  <tr><th scope="row">Certificate Date:</th><td>{formatDisplayDate(documentRow.certificate_issued_at)}</td></tr>
                  <tr><th scope="row">Pre-Acknowledgement Reference Number (Pre-ARN):</th><td>{documentRow.preARN || "-"}</td></tr>
                </tbody></table>
                <h5 className="certificate-section-heading">Donee Organization Details</h5>
                <table className="certificate-table" aria-label="Donee organization details"><tbody>
                  <tr><th scope="row">Name of Donee Organization:</th><td>SRISAILAM FOUNDATION</td></tr>
                  <tr><th scope="row">PAN of Donee Organization:</th><td>ABPCS2866R</td></tr>
                  <tr><th scope="row">80G Registration Number:</th><td>ABPCS2866RF20251 / Validity Date - 19.04.2028</td></tr>
                </tbody></table>

                <h5 className="certificate-section-heading">Details of Donor:</h5>
                <table className="certificate-table" aria-label="Donor details"><tbody>
                  <tr><th scope="row">Name:</th><td>{documentRow.fullName || "-"}</td></tr>
                  <tr><th scope="row">Address:</th><td>{documentRow.address || "-"}</td></tr>
                  <tr><th scope="row">PAN:</th><td>{documentRow.panNo || "-"}</td></tr>
                  <tr><th scope="row">Contact:</th><td>{documentRow.mobile || "-"}</td></tr>
                </tbody></table>

                <h5 className="certificate-section-heading">Details of Donation:</h5>
                <table className="certificate-table" aria-label="Donation details"><tbody>
                  <tr><th scope="row">Mode of Receipt:</th><td>{documentRow.paymentMethod || "-"}</td></tr>
                  <tr><th scope="row">Amount:</th><td>₹{Number(documentRow.payment_status === 'verified' ? documentRow.verified_amount : documentRow.amount).toLocaleString("en-IN")}</td></tr>
                  <tr><th scope="row">Date of Receipt:</th><td>{documentRow.payment_status === 'verified' ? formatDisplayDate(documentRow.payment_date) : '-'}</td></tr>
                </tbody></table>
                {documentRow.payment_status === 'verified' && <p className="certificate-declaration"><strong>Declaration:</strong> We hereby certify that the above donation has been received and will be utilized for charitable purposes. The donation qualifies for deduction under Section 80G of the Income Tax Act, 1961.</p>}
                {!documentRow.certificate_issued_at && <p>Certificate wording and eligibility are pending owner and legal approval.</p>}

                {documentRow.payment_status === 'verified' && <div className="certificate-signature">
                  <p>For SRISAILAM FOUNDATION</p>
                  <img src={SignatureImage} alt="Signature and Seal" width={200} height={100} />
                  <p>Authorized Signatory</p>
                </div>}

                <div className="donation-document-note">
                  <p><strong>Important Note to Donor:</strong></p>
                  <ul>
                    <li>Keep this certificate safe.</li>
                    <li>Quote this certificate while claiming deduction under Section 80G in your ITR.</li>
                    <li>Ensure that your PAN is correctly mentioned.</li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="dash-modal-footer">
              <button type="button" className="btn btn-outline-secondary" onClick={() => setShowCertificateModal(false)}>
                Close
              </button>
              <button
                type="button"
                className="btn btn-primary"
                disabled={documentRow.payment_status !== 'verified' || !documentRow.certificateNo || !documentRow.certificate_issued_at || !documentRow.payment_date || !documentRow.verified_amount}
                onClick={() => generatePDF("certificate-content", `Certificate_${documentRow.certificateNo || "Donation"}.pdf`)}
              >
                Download PDF
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </DashboardLayout>
  );
};

export default DonationList;
